(() => {
  'use strict';

  const controllers = new Map();
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const rootSelector = '[data-ms-carousel]';

  function mount(root) {
    if (controllers.has(root)) return;
    const tracks = [...root.querySelectorAll('[data-ms-carousel-track]')].filter(el => el.closest(rootSelector) === root);
    const navs = [...root.querySelectorAll('[data-ms-carousel-nav]')].filter(el => el.closest(rootSelector) === root);
    if (tracks.length !== 1 || navs.length !== 1) return;
    const [track] = tracks;
    const [nav] = navs;

    const previous = nav.querySelector('[data-ms-carousel-previous]');
    const next = nav.querySelector('[data-ms-carousel-next]');
    const current = nav.querySelector('[data-ms-carousel-current]');
    const total = nav.querySelector('[data-ms-carousel-total]');
    const progress = nav.querySelector('[data-ms-carousel-progress]');
    const status = nav.querySelector('[data-ms-carousel-status]');
    if (![previous, next, current, total, progress, status].every(Boolean)) return;

    const controller = new AbortController();
    const { signal } = controller;
    let frameId = null;
    let mounted = true;

    function pagePositions() {
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (max < 2) return [0];

      const items = [...track.children].filter(el => el.getBoundingClientRect().width > 0);
      if (!items.length) return [0];

      const first = items[0].getBoundingClientRect().left;
      const positions = [0];

      for (const item of items.slice(1)) {
        const delta = item.getBoundingClientRect().left - first;
        const offset = Math.max(0, Math.min(max, delta));
        if (offset - positions[positions.length - 1] > 3) positions.push(offset);
      }

      const last = items[items.length - 1];
      if (last.getBoundingClientRect().width > track.clientWidth + 3 && max - positions[positions.length - 1] > 3) positions.push(max);
      return positions;
    }

    function activeIndex(positions) {
      let closest = 0;
      let distance = Infinity;
      positions.forEach((offset, i) => {
        const diff = Math.abs(track.scrollLeft - offset);
        if (diff < distance) {
          distance = diff;
          closest = i;
        }
      });
      return closest;
    }

    function sync() {
      frameId = null;
      if (!mounted) return;
      const positions = pagePositions();
      const index = activeIndex(positions);
      const count = positions.length;
      const page = index + 1;

      current.textContent = String(page).padStart(2, '0');
      total.textContent = String(count).padStart(2, '0');
      progress.style.transform = `scaleX(${page / count})`;
      const newStatus = `${page} / ${count}`;
      if (status.textContent !== newStatus) status.textContent = newStatus;

      nav.hidden = (count === 1) || (nav.classList.contains('ms-carousel-nav-v2--mobile-only') && !mobileQuery.matches);
      previous.disabled = index === 0;
      next.disabled = index === count - 1;
    }

    function scheduleSync() {
      if (!mounted || frameId !== null) return;
      frameId = requestAnimationFrame(sync);
    }

    function move(direction) {
      const positions = pagePositions();
      const index = activeIndex(positions);
      const destination = positions[Math.max(0, Math.min(positions.length - 1, index + direction))];
      track.scrollTo({ left: destination, behavior: reducedMotion.matches ? 'instant' : 'smooth' });
    }

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', scheduleSync, { passive: true, signal });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      if (nav.classList.contains('ms-carousel-nav-v2--mobile-only') && !mobileQuery.matches) return;
      event.preventDefault();
      move(event.key === 'ArrowLeft' ? -1 : 1);
    }, { signal });

    mobileQuery.addEventListener('change', scheduleSync, { signal });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleSync) : null;
    observer?.observe(track);
    track.querySelectorAll(':scope > *').forEach(item => observer?.observe(item));
    window.addEventListener('resize', scheduleSync, { passive: true, signal });

    controllers.set(root, {
      controller,
      observer,
      cleanup() {
        mounted = false;
        if (frameId !== null) cancelAnimationFrame(frameId);
        controller.abort();
        observer?.disconnect();
      },
    });
    sync();
  }

  function unmount(root) {
    const instance = controllers.get(root);
    if (!instance) return;
    instance.cleanup();
    controllers.delete(root);
  }

  function scan(scope = document) {
    if (scope.matches?.(rootSelector)) mount(scope);
    scope.querySelectorAll?.(rootSelector).forEach(mount);
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (event.target.matches?.(rootSelector)) unmount(event.target);
    event.target.querySelectorAll?.(rootSelector).forEach(unmount);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }
})();

